import React, {
  MouseEvent, CSSProperties, PropsWithChildren, ReactElement,
  memo, useCallback,
} from 'react';
import { createUseStyles } from 'react-jss';
import cn from 'classnames';

import LoaderIndicator from 'components/common/LoaderIndicator';

import { getRegularOpacity, getButtonStyles } from './utils';

const defaultProps = {
  type: 'button' as 'button' | 'button-icon',
  // htmlColor: '#454545' as string,
  htmlTextColor: '#ffffff' as string,
  height: 40 as number,
  radius: 'full' as 'none' | 'half' | 'full',
  variant: 'contained' as 'contained' | 'outlined' | 'text',
  isDisabled: false,
  isLoading: false,
};

type ButtonProps = {
  className?: string,
  style?: CSSProperties,
  type?: 'button' | 'button-icon',
  htmlColor?: string,
  htmlTextColor?: string,
  width?: 'full' | number,
  height?: number,
  radius?: 'none' | 'half' | 'full',
  variant?: 'contained' | 'outlined' | 'text',
  startIcon?: ReactElement,
  endIcon?: ReactElement,
  context?: Record<string, any>,
  noPadding?: boolean,
  isDisabled?: boolean,
  isLoading?: boolean,
  onPress?: (event: MouseEvent | TouchEvent, context?: Record<string, any>) => void,
} & typeof defaultProps;

const Button = (props: PropsWithChildren<ButtonProps & typeof defaultProps>): ReactElement => {
  const {
    className,
    style,
    type,
    children,
    htmlColor,
    htmlTextColor,
    width,
    height,
    radius,
    variant,
    startIcon,
    endIcon,
    context,
    noPadding,
    isDisabled,
    isLoading,
    onPress,
  } = props;

  const classes = useClasses();

  const handlePress = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    if (isDisabled) {
      return;
    }
    onPress?.(event, context);
  }, [onPress, isDisabled, context]);

  const buttonStyles = getButtonStyles(type, radius, variant, width, height, noPadding, htmlColor, htmlTextColor);

  return (
    <button
      type="button"
      onClick={(!isLoading && !isDisabled) ? handlePress : undefined}
      className={cn(className, classes.Button)}
      disabled={isDisabled}
      style={{
        ...buttonStyles,
        ...style,
        ...{
          opacity: getRegularOpacity(isLoading, isDisabled),
          pointerEvents: (!isLoading && !isDisabled) ? 'all' : 'none',
        },
      }}
    >
      {!isLoading && (
        <>
          {Boolean(startIcon) && <div className={classes.startIcon}>{startIcon}</div>}
          {children}
          {Boolean(endIcon) && <div className={classes.endIcon}>{endIcon}</div>}
        </>
      )}
      {isLoading && <div className={classes.loading}><LoaderIndicator small /></div>}
    </button>
  );
};

Button.defaultProps = defaultProps;

const useClasses = createUseStyles({
  Button: {
    '&': {
      display: 'flex',
      position: 'relative',
      overflow: 'hidden',
      fontSize: '1.5rem',
      fontWeight: 600,
      alignItems: 'center',
      cursor: 'pointer',
      transition: 'filter 300ms ease',
    },
    '&:active': {
      filter: 'brightness(0.85)',
    },
  },
  startIcon: {
    paddingRight: '1rem',
  },
  endIcon: {
    paddingLeft: '1rem',
  },
  loading: {
    display: 'flex',
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    alignItems: 'center',
    justifyContent: 'center',
  },
});

export default memo(Button);
